import { addFaqAccordion } from "@/shared/blog/faqAccordion";
import { addHeadingIds } from "@/shared/blog/headingIds";
import { sanitizeBlogHtml } from "@/shared/blog/sanitizeHtml";
import { cn } from "@/utils/cn";
import { forwardRef, useMemo } from "react";

/**
 * Tipografia do corpo do post, em tokens `ds-*`.
 *
 * Estas classes precisam valer para HTML **injetado** (`dangerouslySetInnerHTML`
 * a partir do sanitizador), então tudo aqui é seletor descendente
 * (`[&_h2]:…`) — não há componente React para cada tag.
 *
 * Elas caem em `@layer utilities`. Os blocos ricos (`[data-ds-block="…"]`)
 * moram **fora** de camada no `globals.css` e por isso vencem estas regras
 * sempre que houver empate — é assim que o `key-takeaways`, o `callout` e
 * companhia mantêm a forma deles dentro do texto. Nada aqui duplica o que já
 * está lá: esta lista cobre só as tags soltas do artigo.
 *
 * Contrastes (sobre `ds-paper` #F9F9F9, o fundo da página do post):
 * corpo `ink-800` 11,76:1 · títulos `ink-900` 16,80:1 · apoio `ink-600` 5,50:1 ·
 * link `brand-700` 7,06:1. Todos passam AA para texto normal.
 */
const proseClass = cn(
  "font-ds-sans text-ds-body text-ds-ink-800 max-w-none",

  // Títulos: Merriweather no h2 (30px) e Sora no h3 (24px), como manda a escala
  // — serifada só nos títulos grandes.
  "[&_h2]:font-ds-display [&_h2]:text-ds-h2 [&_h2]:text-ds-ink-900 [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:scroll-mt-28",
  "[&_h3]:font-ds-sans [&_h3]:text-ds-h3 [&_h3]:text-ds-ink-900 [&_h3]:mt-8 [&_h3]:mb-2",

  "[&_p]:my-4 [&_p]:leading-relaxed",
  "[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6",
  "[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6",
  "[&_li]:my-1.5",
  "[&_strong]:text-ds-ink-900 [&_strong]:font-semibold",

  // A barra da citação é elemento não-textual: `brand-500` sobre `ds-paper` dá
  // 3,50:1, acima do mínimo de 3:1. O texto dentro fica em `ink-700` (9,20:1).
  "[&_blockquote]:border-ds-brand-500 [&_blockquote]:text-ds-ink-700 [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic",

  "[&_code]:font-ds-mono [&_code]:bg-ds-ink-100 [&_code]:text-ds-ink-900 [&_code]:rounded-ds-sm [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-ds-small",

  // Link com sublinhado sempre: cor sozinha não distingue link de texto.
  "[&_a]:text-ds-brand-700 [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-ds-brand-800",
  "[&_a]:rounded-ds-sm [&_a:focus-visible]:outline-none [&_a:focus-visible]:ring-2 [&_a:focus-visible]:ring-ds-focus [&_a:focus-visible]:ring-offset-2",

  "[&_img]:rounded-ds-lg [&_img]:my-6 [&_img]:w-full",
  "[&_figure]:my-6 [&_figcaption]:text-ds-ink-600 [&_figcaption]:text-ds-small [&_figcaption]:mt-2 [&_figcaption]:text-center",

  // Tabela solta no texto (E1.1). `block` + `overflow-x-auto` faz a própria
  // tabela rolar na horizontal no celular, sem embrulhar o HTML num <div> — o
  // que exigiria mutar o HTML sanitizado aqui e no SSR, e manter os dois iguais.
  //
  // Sobreposição conhecida e resolvida: dentro de `[data-ds-block="table-compare"]`
  // a tabela é filha de um wrapper que já rola, e lá o `globals.css` (fora de
  // `@layer`, portanto vencedor) devolve `display: table` + `min-width: 34rem`.
  // As duas regras convivem porque tratam de casos diferentes — tabela sozinha
  // rola a si mesma; tabela dentro do bloco rola no wrapper. Não repita
  // `display:block` na regra do bloco, senão as colunas perdem a largura.
  "[&_table]:my-6 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:border-collapse [&_table]:text-ds-small",
  "[&_thead]:bg-ds-surface-sunken",
  "[&_th]:border-ds-border [&_th]:text-ds-ink-900 [&_th]:border [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold",
  "[&_td]:border-ds-border [&_td]:text-ds-ink-800 [&_td]:border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top",
  "[&_caption]:text-ds-ink-600 [&_caption]:text-ds-small [&_caption]:mb-2 [&_caption]:text-left"
);

export interface PostBodyProps {
  /** HTML do post vindo da API. Sempre passa pelo sanitizador antes de entrar no DOM. */
  html: string;
}

/**
 * Corpo do artigo.
 *
 * Expõe `ref` para o contêiner porque o índice de conteúdo (`PostToc`) e a
 * barra de progresso (`ReadingProgress`) leem os `h2` **do DOM** depois da
 * montagem: o HTML é injetado, então não há árvore React para percorrer.
 */
export const PostBody = forwardRef<HTMLDivElement, PostBodyProps>(
  ({ html }, ref) => {
    // `id` nos h2 (âncoras do índice) e o abrir/fechar do FAQ fazem parte do
    // HTML — ver `addHeadingIds` e `addFaqAccordion`.
    const safeHtml = useMemo(
      () => addFaqAccordion(addHeadingIds(sanitizeBlogHtml(html))),
      [html]
    );
    return (
      <div
        ref={ref}
        className={proseClass}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }
);

PostBody.displayName = "PostBody";
